import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  PlusIcon,
  TrashIcon,
  ArrowPathIcon,
  SignalIcon,
  SignalSlashIcon,
  CodeBracketIcon,
  CubeIcon,
  BookOpenIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import { integrationService } from '../services/integrationService';
import { Button, Spinner } from '../../../components';
import AddIntegrationModal from './AddIntegrationModal';
import ClerkFactsToggle from './ClerkFactsToggle';

const TYPE_ICONS: Record<string, typeof CubeIcon> = {
  native: CubeIcon,
  openapi: CodeBracketIcon,
  knowledge_only: BookOpenIcon,
};

const TYPE_LABELS: Record<string, string> = {
  native: 'Native Connector',
  openapi: 'OpenAPI',
  knowledge_only: 'Knowledge Only',
};

const HEALTH_COLORS: Record<string, string> = {
  healthy: 'text-success',
  degraded: 'text-warning',
  unreachable: 'text-danger',
  unknown: 'text-ink-3',
};

export default function IntegrationSettings() {
  const queryClient = useQueryClient();
  const [showAddModal, setShowAddModal] = useState(false);
  const [_selectedId, setSelectedId] = useState<string | null>(null);

  const { data: integrations = [], isLoading } = useQuery({
    queryKey: ['integrations'],
    queryFn: () => integrationService.list(),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => integrationService.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['integrations'] });
      toast.success('Integration removed');
    },
    onError: () => toast.error('Failed to remove integration'),
  });

  const testMutation = useMutation({
    mutationFn: (id: string) => integrationService.testConnection(id),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['integrations'] });
      if (result.reachable) {
        toast.success(`Connected (${result.latencyMs}ms)`);
      } else {
        toast.error(`Unreachable: ${result.error || 'Connection failed'}`);
      }
    },
    onError: () => toast.error('Test failed'),
  });

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-display text-lg font-semibold text-ink">Integrations</h3>
          <p className="mt-1 text-sm text-ink-2">
            Connect your business platforms so the AI agent can take action on behalf of customers.
          </p>
        </div>
        <Button onClick={() => setShowAddModal(true)} size="sm">
          <PlusIcon className="mr-1.5 h-4 w-4" />
          Add Integration
        </Button>
      </div>

      {integrations.length === 0 ? (
        <div className="rounded-card border border-dashed border-line p-12 text-center">
          <CodeBracketIcon className="mx-auto h-12 w-12 text-ink-3" />
          <h4 className="mt-4 text-sm font-medium text-ink">No integrations connected</h4>
          <p className="mt-2 text-sm text-ink-2">
            Connect a native platform like Shopify, or upload an OpenAPI spec to connect any API.
          </p>
          <Button onClick={() => setShowAddModal(true)} size="sm" className="mt-4">
            <PlusIcon className="mr-1.5 h-4 w-4" />
            Add Your First Integration
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {integrations.map((integration) => {
            const Icon = TYPE_ICONS[integration.type] || CubeIcon;
            const healthColor = HEALTH_COLORS[integration.healthStatus] || HEALTH_COLORS.unknown;

            return (
              <div
                key={integration.id}
                className="rounded-card border border-line bg-surface p-4 shadow-s transition-colors hover:border-line-2"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-surface-2">
                      <Icon className="h-5 w-5 text-ink-2" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h4 className="font-medium text-ink">{integration.name}</h4>
                        <span className="rounded-full bg-surface-2 px-2 py-0.5 text-xs text-ink-2">
                          {TYPE_LABELS[integration.type] || integration.type}
                        </span>
                        {integration.platform && (
                          <span className="rounded-full bg-brand/10 px-2 py-0.5 text-xs text-brand">
                            {integration.platform}
                          </span>
                        )}
                      </div>
                      <div className="mt-1 flex items-center gap-3 text-xs">
                        <span className={`flex items-center gap-1 ${healthColor}`}>
                          {integration.healthStatus === 'healthy' ? (
                            <SignalIcon className="h-3 w-3" />
                          ) : (
                            <SignalSlashIcon className="h-3 w-3" />
                          )}
                          {integration.healthStatus}
                        </span>
                        {integration.endpointCount > 0 && (
                          <span className="text-ink-3">
                            {integration.endpointCount} endpoint
                            {integration.endpointCount !== 1 ? 's' : ''}
                          </span>
                        )}
                        {!integration.active && <span className="text-warning">Inactive</span>}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => testMutation.mutate(integration.id)}
                      disabled={testMutation.isPending}
                      className="rounded-lg p-2 text-ink-2 transition hover:bg-black/[0.04] hover:text-ink disabled:opacity-50"
                      title="Test connection"
                    >
                      <ArrowPathIcon
                        className={`h-4 w-4 ${testMutation.isPending ? 'animate-spin' : ''}`}
                      />
                    </button>
                    <button
                      onClick={() => {
                        if (
                          confirm(
                            `Remove "${integration.name}"? This will delete all its endpoints and logs.`
                          )
                        ) {
                          deleteMutation.mutate(integration.id);
                        }
                      }}
                      className="rounded-lg p-2 text-ink-2 transition hover:bg-danger/10 hover:text-danger"
                      title="Remove integration"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => setSelectedId(integration.id)}
                      className="rounded-lg p-2 text-ink-2 transition hover:bg-black/[0.04] hover:text-ink"
                      title="View details"
                    >
                      <ChevronRightIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                {integration.platform === 'clerk' && integration.active && <ClerkFactsToggle />}
              </div>
            );
          })}
        </div>
      )}

      {showAddModal && (
        <AddIntegrationModal
          open={showAddModal}
          onClose={() => setShowAddModal(false)}
          onCreated={() => {
            setShowAddModal(false);
            queryClient.invalidateQueries({ queryKey: ['integrations'] });
          }}
        />
      )}
    </div>
  );
}
